import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import FolderRoundedIcon from '@mui/icons-material/FolderRounded'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded'
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded'
import { useNavigate, useParams } from 'react-router-dom'
import { adminService, type EventReport } from '../../services/adminService'
import AnalyticsSection from '../components/AnalyticsSection'
import { LoadingState } from '../../components/ui/States'
import SessionTypeChip from '../../components/ui/SessionTypeChip'
import type { SessionType } from '../../data/types'
import { apiError } from '../errors'
import { useToast } from '../useToast'

const KPI_ICONS = {
  sessions: EventNoteRoundedIcon,
  speakers: GroupsRoundedIcon,
  content: FolderRoundedIcon,
  forms: AssignmentRoundedIcon,
  responses: InsightsRoundedIcon,
  respondents: HowToRegRoundedIcon,
}

export default function AdminReportsPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { notify } = useToast()
  const [report, setReport] = useState<EventReport | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    adminService
      .eventSummary(id)
      .then(setReport)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))
  }, [id, notify])

  if (loading || !report) return <LoadingState />

  const { content, engagement, agendaByType, activity } = report
  const maxTypeCount = Math.max(1, ...agendaByType.map((a) => a.count))
  const activationPct = Math.round(activity.activationRate * 100)

  const kpis = [
    { key: 'sessions', label: 'Sessions', value: content.sessions, icon: KPI_ICONS.sessions },
    { key: 'speakers', label: 'Speakers', value: content.speakers, icon: KPI_ICONS.speakers },
    { key: 'content', label: 'Content items', value: content.contentItems, icon: KPI_ICONS.content },
    { key: 'forms', label: 'Forms', value: content.forms, icon: KPI_ICONS.forms },
    { key: 'responses', label: 'Form responses', value: engagement.totalResponses, icon: KPI_ICONS.responses },
    { key: 'respondents', label: 'Unique respondents', value: engagement.uniqueRespondents, icon: KPI_ICONS.respondents },
  ]

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate(`/admin/events/${id}`)}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        Back to event
      </Button>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          {report.event.name}
        </Typography>
        <Chip
          label={report.event.status}
          size="small"
          sx={{ fontWeight: 700, textTransform: 'capitalize' }}
        />
      </Stack>

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {kpis.map((k) => (
          <Grid item xs={6} sm={4} md={2} key={k.key}>
            <Card variant="outlined" sx={{ p: 2, borderRadius: 3, height: '100%' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,117,188,0.1)',
                  color: 'primary.main',
                  mb: 1.5,
                }}
              >
                <k.icon fontSize="small" />
              </Box>
              <Typography variant="h4" fontWeight={700} lineHeight={1}>
                {k.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {k.label}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Attendee engagement */}
      <Card variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Attendee engagement
        </Typography>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Stack alignItems="center" spacing={1}>
              <Box
                sx={{
                  position: 'relative',
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: (theme) =>
                    `conic-gradient(${theme.palette.primary.main} ${activationPct * 3.6}deg, rgba(0,117,188,0.12) 0deg)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 92,
                    height: 92,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="h5" fontWeight={700} lineHeight={1}>
                    {activationPct}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    activated
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {activity.activated} of {activity.invited} invited
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Grid container spacing={1.5}>
              {[
                { label: 'Active attendees', value: activity.activeAttendees },
                { label: 'App opens', value: activity.appOpens },
                { label: 'Agenda views', value: activity.agendaViews },
                { label: 'Session views', value: activity.sessionViews },
                { label: 'Content views', value: activity.contentViews },
                { label: 'Downloads', value: activity.contentDownloads },
              ].map((m) => (
                <Grid item xs={6} sm={4} key={m.label}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'background.default',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="h6" fontWeight={700} lineHeight={1}>
                      {m.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {(activity.topSessions.length > 0 || activity.topContent.length > 0) && (
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {activity.topSessions.length > 0 && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  MOST-VIEWED SESSIONS
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {activity.topSessions.map((s) => (
                    <Stack key={s.id} direction="row" justifyContent="space-between">
                      <Typography variant="body2" noWrap sx={{ mr: 1 }}>
                        {s.title}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {s.views}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Grid>
            )}
            {activity.topContent.length > 0 && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  MOST-ACCESSED CONTENT
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {activity.topContent.map((c) => (
                    <Stack key={c.id} direction="row" justifyContent="space-between">
                      <Typography variant="body2" noWrap sx={{ mr: 1 }}>
                        {c.title}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {c.hits}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Grid>
            )}
          </Grid>
        )}
      </Card>

      <Grid container spacing={3}>
        {/* Agenda breakdown */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Agenda breakdown
            </Typography>
            {agendaByType.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No sessions yet.
              </Typography>
            ) : (
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {agendaByType.map((a) => (
                  <Box key={a.type}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <SessionTypeChip type={a.type as SessionType} />
                      <Typography variant="body2" fontWeight={700}>
                        {a.count}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={(a.count / maxTypeCount) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                ))}
              </Stack>
            )}
            {content.tracks.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  TRACKS
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {content.tracks.map((t) => (
                    <Chip key={t} label={t} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Form engagement */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Form engagement
              </Typography>
              <Chip
                label={`${engagement.openForms} open`}
                size="small"
                sx={{ bgcolor: 'rgba(0,117,188,0.1)', color: 'primary.main', fontWeight: 600 }}
              />
            </Stack>
            {engagement.forms.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No forms yet.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Form</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Responses
                      </TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {engagement.forms.map((f) => (
                      <TableRow key={f.id} hover>
                        <TableCell>{f.title}</TableCell>
                        <TableCell>
                          <Chip
                            label={f.status}
                            size="small"
                            sx={{ height: 20, fontSize: '0.68rem', textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700}>{f.responses}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() =>
                              navigate(`/admin/events/${id}/forms/${f.id}/responses`)
                            }
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Grid>
      </Grid>

      <AnalyticsSection eventId={id} />
    </Container>
  )
}
